import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { X, BookOpen } from 'lucide-react';
import { fmt, fmtDT, StatusPill, Spinner, ErrorBanner, PaginationBar, UNAVAILABLE } from './ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;
const PER = 50;

function Stat({ label, value }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', minWidth: 110 }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default function CourseLearnersDrawer({ courseName, pii, onClose, onLearner }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const close = useCallback(() => { setOpen(false); setTimeout(onClose, 220); }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  useEffect(() => {
    if (!courseName) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null); setPage(1);
      try {
        const r = await axios.get(`${API}/api/lup/v2/report/course-activity`, {
          params: { search: String(courseName), per_page: 500, page: 1, sort: 'enrolled_at:desc' },
          withCredentials: true,
        });
        if (cancelled) return;
        const all = r.data?.data || [];
        const mine = all.filter(x => String(x.course_name) === String(courseName));
        setRows(mine);
        setTruncated((r.data?.last_page || 1) > 1);
      } catch (e) {
        if (!cancelled) { setErr(UNAVAILABLE('Course learners')); setRows([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [courseName]);

  const distinctLearners = useMemo(() => new Set(rows.map(r => r.student_user_id)).size, [rows]);
  const completed = rows.filter(r => String(r.enrollment_status || '').toLowerCase() === 'completed').length;
  const inProgress = rows.filter(r => String(r.enrollment_status || '').toLowerCase() === 'in progress').length;
  const totalPages = Math.max(1, Math.ceil(rows.length / PER));
  const pageRows = rows.slice((page - 1) * PER, page * PER);

  return (
    <div data-testid="course-learners-drawer" style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)', opacity: open ? 1 : 0, transition: 'opacity 0.2s ease' }} />
      <aside style={{
        position: 'absolute', top: 0, right: 0, height: '100%', width: 'min(960px, 94vw)',
        background: '#fff', boxShadow: '-12px 0 40px rgba(15,23,42,0.2)', display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BookOpen size={20} color="#4338ca" />
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Enrolled learners</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }} data-testid="course-drawer-title">{courseName}</div>
            </div>
          </div>
          <button onClick={close} data-testid="course-drawer-close-btn" aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#64748b' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="course-drawer-error" /> : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <Stat label="Learners" value={fmt(distinctLearners)} />
                <Stat label="Enrollments" value={fmt(rows.length)} />
                <Stat label="Completed" value={fmt(completed)} />
                <Stat label="In Progress" value={fmt(inProgress)} />
              </div>
              {truncated && <p style={{ fontSize: 12, color: '#a16207', margin: '0 0 12px' }} data-testid="course-drawer-truncated">Showing the first 500 enrollments for this course.</p>}
              {rows.length === 0 ? (
                <div className="empty-state" data-testid="course-drawer-empty"><h3>No learners found</h3><p>No enrollments found for this course.</p></div>
              ) : (
                <>
                  <div className="table-scroll" style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    <table data-testid="course-drawer-table">
                      <thead><tr>
                        <th>Platform</th>{pii && <th>Learner</th>}{pii && <th>Email</th>}<th>LMS ID</th><th>Org</th><th>Country</th><th>Status</th><th className="td-right">Progress</th><th>Enrolled</th><th>Completed</th>
                      </tr></thead>
                      <tbody>
                        {pageRows.map((row, i) => (
                          <tr key={i} data-testid={`course-drawer-row-${i}`}>
                            <td style={{ fontSize: 12 }}>{row.platform || '—'}</td>
                            {pii && <td style={{ fontSize: 13 }}>{row.learner_name || '—'}</td>}
                            {pii && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.email || '—'}</td>}
                            <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                              {row.student_user_id != null && onLearner
                                ? <button onClick={() => onLearner({ lmsId: row.student_user_id, name: row.learner_name })} data-testid={`course-drawer-lmsid-link-${i}`} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, fontFamily: 'monospace', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2 }}>{row.student_user_id}</button>
                                : (row.student_user_id ?? '—')}
                            </td>
                            <td style={{ fontSize: 12 }}>{row.organization || '—'}</td>
                            <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
                            <td><StatusPill status={row.enrollment_status} /></td>
                            <td className="td-right" style={{ fontWeight: 600 }}>{row.progress_pct != null ? `${row.progress_pct}%` : '—'}</td>
                            <td style={{ fontSize: 12 }}>{fmtDT(row.enrolled_at)}</td>
                            <td style={{ fontSize: 12 }}>{fmtDT(row.completed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar page={page} totalPages={totalPages} total={rows.length} perPage={PER} onPage={setPage} testId="course-drawer-pagination" />
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
